import { nextTick, ref, type Ref } from 'vue'

type SortableCtor = typeof import('sortablejs')
type SortableInstance = InstanceType<SortableCtor>

type SortableRuntime = {
  instances: SortableInstance[]
  module: SortableCtor | null
}

function prefersReducedMotion(): boolean {
  if (import.meta.server || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

async function loadSortable(runtime: SortableRuntime): Promise<SortableCtor | null> {
  if (import.meta.server) {
    return null
  }
  if (!runtime.module) {
    runtime.module = (await import('sortablejs')).default
  }
  return runtime.module
}

function destroySortables(runtime: SortableRuntime): void {
  for (const instance of runtime.instances) {
    instance.destroy()
  }
  runtime.instances.length = 0
}

function readKeys(list: HTMLElement): string[] {
  return [...list.querySelectorAll(':scope > [data-reorder-key]')]
    .map(function readKey(node: Element) {
      return node.getAttribute('data-reorder-key')
    })
    .filter(function keepKey(key: string | null): key is string {
      return Boolean(key)
    })
}

function bindList(
  Sortable: SortableCtor,
  runtime: SortableRuntime,
  list: HTMLElement,
  onOrder: (groupId: string, keys: string[]) => void,
): void {
  const groupId = list.getAttribute('data-reorder-group')
  if (!groupId) {
    return
  }
  runtime.instances.push(
    Sortable.create(list, {
      group: { name: `monorga-reorder-${groupId}`, pull: false, put: false },
      handle: '.org-reorder-handle',
      draggable: '[data-reorder-key]',
      animation: prefersReducedMotion() ? 0 : 420,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      forceFallback: true,
      fallbackOnBody: true,
      ghostClass: 'org-reorder-ghost',
      chosenClass: 'org-reorder-chosen',
      dragClass: 'org-reorder-drag',
      onMove(event) {
        return event.to === event.from
      },
      onEnd() {
        onOrder(groupId, readKeys(list))
      },
    }),
  )
}

async function bindSortables(
  runtime: SortableRuntime,
  root: HTMLElement | null,
  onOrder: (groupId: string, keys: string[]) => void,
): Promise<void> {
  destroySortables(runtime)
  const Sortable = await loadSortable(runtime)
  if (!Sortable || !root) {
    return
  }
  await nextTick()
  const lists = root.querySelectorAll('[data-reorder-group]')
  for (const list of lists) {
    if (list instanceof HTMLElement) {
      bindList(Sortable, runtime, list, onOrder)
    }
  }
}

export function useOrgReorderSortable(
  onOrder: (groupId: string, keys: string[]) => void,
): {
  rootRef: Ref<HTMLElement | null>
  bind: () => Promise<void>
  destroy: () => void
} {
  const rootRef = ref<HTMLElement | null>(null)
  const runtime: SortableRuntime = { instances: [], module: null }

  return {
    rootRef,
    bind: () => bindSortables(runtime, rootRef.value, onOrder),
    destroy: () => destroySortables(runtime),
  }
}
